/**
 * A polite poller: checks something on a timer, but only as often as it must.
 * Every check is a relay call and each one costs Cloudflare KV operations, so
 * this is built to do as few as it can while still feeling live:
 *
 *  - It only runs while the screen is visible. A phone with its screen off, or
 *    another tab in front, makes no calls at all.
 *  - Coming back to the screen checks straight away (if the last check wasn't
 *    just now), so returning to the app never shows stale numbers for a full
 *    interval.
 *  - It slows down when nothing is happening: the longer since anything
 *    changed, the longer between checks (see `pollDelay`). Any change resets it.
 *  - Failures back off exponentially instead of hammering a relay that is down.
 *  - Picking the phone up counts: a tap on a phone that has slowed down snaps it
 *    back to the lively rate and checks now, so it is never a couple of minutes
 *    behind someone who is actually using it. (While it is already lively a tap
 *    changes nothing, so ordinary use costs no extra calls.)
 *  - Each delay is jittered a little so a table's phones don't all call in the
 *    same instant.
 *  - `tick` can end it (`'stop'`), e.g. the game ended or the link is gone.
 */

export type TickResult = 'changed' | 'unchanged' | 'error' | 'stop';

/** Check this often while things are lively; slower as it gets quieter. */
const QUIET_STEPS: { afterMs: number; multiplier: number }[] = [
  { afterMs: 30 * 60 * 1000, multiplier: 4 },
  { afterMs: 10 * 60 * 1000, multiplier: 2 },
];
const MAX_BACKOFF_MS = 5 * 60 * 1000;

/**
 * How long to wait before the next check.
 * @param baseMs  the interval while things are lively
 * @param quietMs how long since anything last changed
 * @param failures consecutive failed checks so far
 */
export function pollDelay(baseMs: number, quietMs: number, failures: number): number {
  if (failures > 0) return Math.min(baseMs * 2 ** failures, MAX_BACKOFF_MS);
  for (const step of QUIET_STEPS) {
    if (quietMs >= step.afterMs) return baseMs * step.multiplier;
  }
  return baseMs;
}

export interface PollerEnv {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  isVisible(): boolean;
  /** Calls `cb` whenever the screen becomes visible or hidden; returns the unsubscribe. */
  onVisibilityChange(cb: () => void): () => void;
  /** Calls `cb` when the person touches the screen or keyboard; returns the unsubscribe. */
  onActivity(cb: () => void): () => void;
  random(): number;
}

function browserEnv(): PollerEnv {
  return {
    now: () => Date.now(),
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (handle) => window.clearTimeout(handle as number),
    isVisible: () => document.visibilityState !== 'hidden',
    onVisibilityChange: (cb) => {
      document.addEventListener('visibilitychange', cb);
      return () => document.removeEventListener('visibilitychange', cb);
    },
    onActivity: (cb) => {
      document.addEventListener('pointerdown', cb, { passive: true });
      document.addEventListener('keydown', cb, { passive: true });
      return () => {
        document.removeEventListener('pointerdown', cb);
        document.removeEventListener('keydown', cb);
      };
    },
    random: () => Math.random(),
  };
}

export interface Poller {
  stop(): void;
  /** Check right now (e.g. after the person did something that likely changed things). */
  pokeNow(): void;
  isRunning(): boolean;
}

export interface PollerOptions {
  baseMs: number;
  tick(): Promise<TickResult>;
  /** Least time between two checks when returning to the screen. Default 10 s. */
  minGapMs?: number;
  env?: Partial<PollerEnv>;
}

export function startPoller(options: PollerOptions): Poller {
  const env: PollerEnv = { ...browserEnv(), ...options.env };
  const minGapMs = options.minGapMs ?? 10_000;

  let stopped = false;
  let handle: unknown;
  let inFlight = false;
  let failures = 0;
  let lastCheckAt = 0;
  let lastChangeAt = env.now();

  const clear = (): void => {
    if (handle !== undefined) env.clearTimeout(handle);
    handle = undefined;
  };

  const schedule = (): void => {
    clear();
    if (stopped || !env.isVisible()) return;
    const delay = pollDelay(options.baseMs, env.now() - lastChangeAt, failures);
    const jittered = Math.round(delay * (0.9 + 0.2 * env.random()));
    handle = env.setTimeout(() => void run(), jittered);
  };

  const run = async (): Promise<void> => {
    clear();
    if (stopped || inFlight || !env.isVisible()) return;
    inFlight = true;
    lastCheckAt = env.now();
    let result: TickResult;
    try {
      result = await options.tick();
    } catch {
      result = 'error';
    }
    inFlight = false;
    if (stopped) return;
    if (result === 'stop') {
      stop();
      return;
    }
    if (result === 'error') failures++;
    else failures = 0;
    if (result === 'changed') lastChangeAt = env.now();
    schedule();
  };

  const onVisibility = (): void => {
    if (stopped) return;
    if (!env.isVisible()) {
      clear();
      return;
    }
    if (env.now() - lastCheckAt >= minGapMs) void run();
    else schedule();
  };
  const unsubscribeVisibility = env.onVisibilityChange(onVisibility);

  const onActivity = (): void => {
    if (stopped) return;
    // Only matters once slowed down; while lively, a tap must not add calls.
    if (failures === 0 && pollDelay(options.baseMs, env.now() - lastChangeAt, 0) === options.baseMs) return;
    lastChangeAt = env.now();
    if (env.isVisible() && env.now() - lastCheckAt >= minGapMs) void run();
    else schedule();
  };
  const unsubscribeActivity = env.onActivity(onActivity);

  function stop(): void {
    stopped = true;
    clear();
    unsubscribeVisibility();
    unsubscribeActivity();
  }

  schedule();

  return {
    stop,
    pokeNow: () => {
      if (!stopped) void run();
    },
    isRunning: () => !stopped,
  };
}
